import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAddJobPayment, useUpdateJobPayment } from '@/hooks/useJobPayments';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const paymentSchema = z.object({
  amount: z.string().min(1, 'Amount is required'),
  payment_method: z.string().min(1, 'Payment method is required'),
  payment_date: z.string().min(1, 'Payment date is required'),
  notes: z.string().optional(),
  edit_reason: z.string().optional(),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

interface ExistingPayment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  notes?: string;
}

interface AddPaymentDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment?: ExistingPayment | null;
}

export function AddPaymentDialog({ jobId, open, onOpenChange, payment }: AddPaymentDialogProps) {
  const addPayment = useAddJobPayment();
  const updatePayment = useUpdateJobPayment();
  const { formatDateInput, parseDate } = useTimezoneUtils();
  const isEdit = !!payment;

  const form = useForm<PaymentFormData>({
    resolver: zodResolver(
      isEdit
        ? paymentSchema.extend({ edit_reason: z.string().min(1, 'Reason is required') })
        : paymentSchema
    ),
    defaultValues: {
      amount: '',
      payment_method: '',
      payment_date: formatDateInput(new Date()),
      notes: '',
      edit_reason: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (payment) {
        form.reset({
          amount: payment.amount.toString(),
          payment_method: payment.payment_method,
          payment_date: formatDateInput(new Date(payment.payment_date)),
          notes: payment.notes || '',
          edit_reason: '',
        });
      } else {
        form.reset({
          amount: '',
          payment_method: '',
          payment_date: formatDateInput(new Date()),
          notes: '',
          edit_reason: '',
        });
      }
    }
  }, [open, payment, form, formatDateInput]);

  const handleSubmit = (data: PaymentFormData) => {
    if (isEdit && payment) {
      updatePayment.mutate(
        {
          paymentId: payment.id,
          jobId,
          updates: {
            amount: parseFloat(data.amount),
            payment_method: data.payment_method,
            // Midnight in the company timezone. new Date('yyyy-MM-dd') is midnight UTC, which
            // showed as the day before in US tenants (and slipped back a day on every edit).
            payment_date: parseDate(data.payment_date).toISOString(),
            notes: data.notes || null,
          },
          reason: data.edit_reason || '',
        },
        {
          onSuccess: () => {
            form.reset();
            onOpenChange(false);
          },
        }
      );
    } else {
      addPayment.mutate(
        {
          job_id: jobId,
          amount: parseFloat(data.amount),
          payment_method: data.payment_method,
          payment_date: parseDate(data.payment_date).toISOString(),
          notes: data.notes || undefined,
        },
        {
          onSuccess: () => {
            form.reset();
            onOpenChange(false);
          },
        }
      );
    }
  };

  const isPending = addPayment.isPending || updatePayment.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? 'Edit an existing payment for this job.' : 'Record a new payment received for this job.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount ($) *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card / EFTPOS</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Date *</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Optional notes" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isEdit && (
              <FormField
                control={form.control}
                name="edit_reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason for edit *</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="e.g., Corrected amount, Fixed wrong method..."
                        rows={2}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isEdit ? 'Saving...' : 'Recording...'
                  : isEdit ? 'Save Changes' : 'Record Payment'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
